import { useMemo } from 'react';
import { atom, useRecoilValue } from 'recoil';
import type { RecoilState } from 'recoil';
import colors from 'styles/colors';
import { SRNA_PERSIST_TOKEN } from 'config/constants';
import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';
import type { AnnotationColors } from './useAnnotationColors';

export const ANNOTATION_COLOR_OPTIONS = [
  { name: 'White', value: '#E8E8E8' },
  { name: 'Cyan', value: '#66DDE6' },
  { name: 'Green', value: colors.ugcAnnotationHover },
  { name: 'Yellow', value: colors.ugcAnnotation },
  { name: 'Orange', value: '#FFAA66' },
  { name: 'Red', value: '#E04A3A' },
  { name: 'Pink', value: '#E0559A' },
] as const;

// What annotations looked like before the colors could be chosen.
export const DEFAULT_ANNOTATION_COLOR_SETTINGS: AnnotationColors = {
  defaultColor: colors.ugcAnnotation,
  hoverColor: colors.ugcAnnotationHover,
};

export const annotationColorSettingsState: RecoilState<AnnotationColors> = atom<AnnotationColors>({
  key: `${SRNA_PERSIST_TOKEN}annotationColorSettings`,
  default: DEFAULT_ANNOTATION_COLOR_SETTINGS,
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect()],
});

const isOption = (color: unknown): color is string =>
  ANNOTATION_COLOR_OPTIONS.some(({ value }) => value === color);

/** The colors the user chose. A saved color that is no longer offered falls back to the default. */
export const useUserAnnotationColors = (): AnnotationColors => {
  const { defaultColor, hoverColor } = useRecoilValue(annotationColorSettingsState);

  return useMemo(
    () => ({
      defaultColor: isOption(defaultColor)
        ? defaultColor
        : DEFAULT_ANNOTATION_COLOR_SETTINGS.defaultColor,
      hoverColor: isOption(hoverColor) ? hoverColor : DEFAULT_ANNOTATION_COLOR_SETTINGS.hoverColor,
    }),
    [defaultColor, hoverColor]
  );
};
